import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiGetWordleWord, apiValidateWord } from '../utils/api';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';
import ShareModal from '../components/ShareModal';

const MAX_ATTEMPTS = 9;
const WORD_LENGTH  = 5;
const NUM_BOARDS   = 4;

const KEYBOARD_ROWS = [
  ['Q','W','E','R','T','Y','U','I','O','P'],
  ['A','S','D','F','G','H','J','K','L'],
  ['ENTER','Z','X','C','V','B','N','M','⌫'],
];

const FALLBACKS   = ['CRANE','STALE','SHARP','LIGHT','BRAVE','DREAM','GRAPE','PLUMB'];
const STATE_EMOJI = { correct: '🟩', present: '🟨', absent: '⬛' };

function buildShareText(guesses, status) {
  const score = status === 'won' ? guesses.length : 'X';
  const grid  = guesses.map(g =>
    g.states.map(bs => bs.map(s => STATE_EMOJI[s]).join('')).join(' ')
  ).join('\n');
  return `🟩🟩🟩🟩 WordZingo Quordle ${score}/${MAX_ATTEMPTS}\n\n${grid}\n\n🎯 Beat me! wordzingo.com/quordle`;
}

function getLetterStates(guess, target) {
  const result = Array(WORD_LENGTH).fill('absent');
  const tArr   = target.split('');
  const used   = Array(WORD_LENGTH).fill(false);
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (guess[i] === tArr[i]) { result[i] = 'correct'; used[i] = true; }
  }
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (result[i] === 'correct') continue;
    for (let j = 0; j < WORD_LENGTH; j++) {
      if (!used[j] && guess[i] === tArr[j]) { result[i] = 'present'; used[j] = true; break; }
    }
  }
  return result;
}

// Cell size: fills half the viewport minus container padding (24px), board gap (12px),
// and inner row gaps (4×3px = 12px), capped at 36px on large screens.
const RESPONSIVE_CELL = 'clamp(22px, calc(((100vw - 36px) / 2 - 12px) / 5), 36px)';

function QuadBoard({ boardIdx, target, guesses, current, boardWon, shake }) {
  const GAP    = 3;
  const colors = ['#4f46e5','#059669','#d97706','#dc2626'];
  const color  = colors[boardIdx];
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="text-center mb-1">
        <span style={{
          background: boardWon ? '#dcfce7' : `${color}18`,
          color: boardWon ? '#065f46' : color,
          padding: '0.12rem 0.6rem', borderRadius: 99, fontSize: '0.72rem', fontWeight: 700
        }}>
          {boardWon ? `✅ Word ${boardIdx + 1}` : `Word ${boardIdx + 1}`}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: GAP }}>
        {Array.from({ length: MAX_ATTEMPTS }, (_, rowIdx) => {
          const guess    = guesses[rowIdx];
          const isActive = rowIdx === guesses.length && !boardWon;
          const letters  = guess
            ? guess.word.split('')
            : isActive ? current.split('') : [];
          return (
            <div key={rowIdx}
              className={isActive && shake ? 'shake' : ''}
              style={{ display: 'flex', gap: GAP, justifyContent: 'center' }}>
              {Array.from({ length: WORD_LENGTH }, (_, colIdx) => {
                const letter = letters[colIdx] || '';
                let cls = '';
                if (guess) cls = guess.states[boardIdx][colIdx];
                else if (letter && isActive) cls = 'filled';
                return (
                  <div key={colIdx} className={`wordle-cell ${cls}`}
                    style={{
                      width: RESPONSIVE_CELL,
                      height: RESPONSIVE_CELL,
                      fontSize: 'clamp(0.55rem, 2vw, 0.8rem)',
                      flexShrink: 0,
                    }}>
                    {letter}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Quordle() {
  const [targets, setTargets]     = useState([]);
  const [guesses, setGuesses]     = useState([]);
  const [current, setCurrent]     = useState('');
  const [boardWon, setBoardWon]   = useState([false,false,false,false]);
  const [status, setStatus]       = useState('playing');
  const [keyStates, setKeyStates] = useState({});
  const [shake, setShake]         = useState(false);
  const [message, setMessage]     = useState('');
  const [loading, setLoading]     = useState(true);
  const [showShare, setShowShare] = useState(false);

  async function fetchWords() {
    setLoading(true);
    try {
      const results = await Promise.all(Array(NUM_BOARDS).fill(null).map(() => apiGetWordleWord()));
      const words = results.map(r => r.data.word.toUpperCase());
      const unique = [...new Set(words)];
      let fi = 0;
      while (unique.length < NUM_BOARDS) unique.push(FALLBACKS[fi++ % FALLBACKS.length]);
      setTargets(unique.slice(0, NUM_BOARDS));
    } catch {
      setTargets(FALLBACKS.slice(0, NUM_BOARDS));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchWords(); }, []);

  function newGame() {
    setGuesses([]); setCurrent(''); setStatus('playing');
    setBoardWon([false,false,false,false]); setKeyStates({}); setMessage(''); setShowShare(false);
    fetchWords();
  }

  function showMsg(msg, dur = 2500) {
    setMessage(msg);
    setTimeout(() => setMessage(''), dur);
  }

  const handleKey = useCallback(async (key) => {
    if (status !== 'playing') return;
    if (key === 'ENTER') {
      if (current.length < WORD_LENGTH) {
        setShake(true); setTimeout(() => setShake(false), 400);
        showMsg('Not enough letters!'); return;
      }
      try {
        const { data } = await apiValidateWord(current.toLowerCase());
        if (!data.valid) {
          setShake(true); setTimeout(() => setShake(false), 400);
          showMsg('Not in word list!'); return;
        }
      } catch { /* allow if API down */ }

      const newStates  = targets.map(t => getLetterStates(current, t));
      const newGuess   = { word: current, states: newStates };
      const newGuesses = [...guesses, newGuess];
      setGuesses(newGuesses);

      const newBoardWon = targets.map((t, i) => boardWon[i] || current === t);
      setBoardWon(newBoardWon);

      const ks = { ...keyStates };
      current.split('').forEach((ch, i) => {
        const prev = ks[ch];
        const best = newStates.reduce((acc, s) => {
          const v = s[i];
          if (acc === 'correct' || v === 'correct') return 'correct';
          if (acc === 'present' || v === 'present') return 'present';
          return 'absent';
        }, 'absent');
        if (prev === 'correct') return;
        if (prev === 'present' && best !== 'correct') return;
        ks[ch] = best;
      });
      setKeyStates(ks);

      if (newBoardWon.every(Boolean)) {
        setStatus('won');
        showMsg(`🎉 Quordle conquered in ${newGuesses.length}/${MAX_ATTEMPTS}!`, 5000);
        setTimeout(() => setShowShare(true), 4000);
      } else if (newGuesses.length >= MAX_ATTEMPTS) {
        setStatus('lost');
        showMsg('Better luck next time! 😔', 4000);
        setTimeout(() => setShowShare(true), 4000);
      }
      setCurrent('');
    } else if (key === '⌫' || key === 'BACKSPACE') {
      setCurrent(c => c.slice(0, -1));
    } else if (/^[A-Z]$/.test(key) && current.length < WORD_LENGTH) {
      setCurrent(c => c + key);
    }
  }, [current, guesses, boardWon, keyStates, status, targets]);

  useEffect(() => {
    function onKey(e) { handleKey(e.key === 'Backspace' ? '⌫' : e.key.toUpperCase()); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleKey]);

  if (loading) return (
    <div className="game-page text-center">
      <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
      <p className="mt-2 fw-bold">Loading Quordle…</p>
    </div>
  );

  const guessesLeft = MAX_ATTEMPTS - guesses.length;

  return (
    <div className="game-page">
      <PageSEO page="quordle" />
      {showShare && (
        <ShareModal
          shareText={buildShareText(guesses, status)}
          gameUrl="https://wordzingo.com/quordle"
          onClose={() => setShowShare(false)}
          onNewGame={newGame}
        />
      )}
      <div className="container" style={{ maxWidth: 700 }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🟩🟩🟩🟩</span>
          <h1>Quordle</h1>
          <p>Guess 4 secret words simultaneously — 9 guesses total, every guess hits all 4 boards!</p>
        </div>

        {/* Stats strip */}
        <div className="d-flex justify-content-center gap-4 mb-3" style={{ fontSize: '0.85rem' }}>
          <span><strong style={{ color: 'var(--primary)' }}>{guessesLeft}</strong> guess{guessesLeft !== 1 ? 'es' : ''} left</span>
          <span><strong style={{ color: '#059669' }}>{boardWon.filter(Boolean).length}</strong> / 4 solved</span>
        </div>

        {message && (
          <div className="text-center fw-bold py-2 px-3 rounded-3 mb-3"
            style={{ background: '#1e1b4b', color: '#fff', fontSize: '1rem' }}>
            {message}
          </div>
        )}

        {/* 2×2 grid of boards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: '1.5rem' }}>
          {targets.map((target, i) => (
            <QuadBoard
              key={i}
              boardIdx={i}
              target={target}
              guesses={guesses}
              current={current}
              boardWon={boardWon[i]}
              shake={shake}
            />
          ))}
        </div>

        {status === 'lost' && (
          <div className="alert alert-custom mb-3" style={{ background: '#fee2e2', color: '#991b1b' }}>
            <div className="text-center fw-bold mb-2">💀 Better luck next time!</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.4rem 1.4rem' }}>
              {targets.map((t, i) => (
                <span key={i}>
                  Word {i + 1}: <strong style={{ letterSpacing: 2 }}>{t}</strong>{boardWon[i] ? ' ✅' : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Keyboard */}
        <div className="wordle-keyboard">
          {KEYBOARD_ROWS.map((row, i) => (
            <div key={i} className="kb-row">
              {row.map(key => (
                <button
                  key={key}
                  className={`kb-key ${key.length > 1 ? 'wide' : ''} ${keyStates[key] || ''}`}
                  onClick={() => handleKey(key)}
                >{key}</button>
              ))}
            </div>
          ))}
        </div>

        {status !== 'playing' && (
          <div className="text-center mt-4 d-flex flex-wrap justify-content-center gap-2">
            <button className="solve-btn" onClick={newGame}>🔄 New Quordle</button>
            <button className="btn fw-bold"
              style={{ borderRadius: 50, background: '#4f46e5', color: '#fff', border: 'none', fontFamily: 'Nunito,sans-serif', padding: '0.55rem 1.2rem' }}
              onClick={() => setShowShare(true)}>
              📤 Share Result
            </button>
            <Link to="/wordle" className="btn fw-bold"
              style={{ borderRadius: 50, background: 'var(--border)', border: 'none', fontFamily: 'Nunito,sans-serif', padding: '0.55rem 1.2rem' }}>
              🟩 Wordle
            </Link>
            <Link to="/dordle" className="btn fw-bold"
              style={{ borderRadius: 50, background: 'var(--border)', border: 'none', fontFamily: 'Nunito,sans-serif', padding: '0.55rem 1.2rem' }}>
              🟩🟩 Dordle
            </Link>
          </div>
        )}

        <SeoContent game="quordle" />
      </div>
    </div>
  );
}
